import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FormAutocomplete, FormCheckbox, FormTextField } from '../../common/form';
import { CreateExternalPricelistDto } from '../../common/PricePrgClient';
import { ProgressButton } from '../../common/ProgressButton';
import { RootState } from '../../store';
import { transformOptionalNumber } from '../../utils';
import { getPricelists } from '../pricelist/redux/pricelistSelectors';
import { getUnfilteredExternalPricelists } from './redux/externalPricelistSelectors';
import { closeCreateExternalPricelistDialog, createExternalPricelist } from './redux/externalPricelistSlices';

const mapStateToProps = (state: RootState) => ({
    externalPricelistRefs: Object.values(getUnfilteredExternalPricelists(state)).map(
        x => x.externalPricelistRef
    ),
    pricelists: getPricelists(state),
    isCreating: state.externalPricelist.ui.createExternalPricelistDialog.isCreating
});

type Props = ReturnType<typeof mapStateToProps>;

const CreateExternalPricelistDialog = ({ externalPricelistRefs, pricelists, isCreating }: Props) => {
    const upperRefs = externalPricelistRefs.map(x => x.toUpperCase());
    const schema: yup.SchemaOf<CreateExternalPricelistDto> = yup.object().shape({
        name: yup.string().required().max(40).label('Name'),
        pricelistRef: yup
            .string()
            .required()
            .max(10)
            .test(
                'notOneOf',
                'Reference already exists',
                value => value == null || !upperRefs.includes(value.toUpperCase())
            )
            .label('Reference'),
        marginThreshold: yup.number().required().min(0).max(100).label('Margin Threshold'),
        includeVat: yup.boolean().required().label('Include VAT'),
        inPricelistId: yup.number().optional().transform(transformOptionalNumber).label('Pricelist id')
    });
    const dispatch = useDispatch();

    const {
        control,
        handleSubmit,
        formState: { isDirty }
    } = useForm<CreateExternalPricelistDto>({
        resolver: yupResolver(schema),
        defaultValues: { includeVat: false }
    });

    const submit = (form: CreateExternalPricelistDto) => {
        dispatch(createExternalPricelist.request(form));
    };

    const handleClose = () => {
        dispatch(closeCreateExternalPricelistDialog());
    };

    return (
        <Dialog open={true}>
            <DialogTitle>Create new external pricelist</DialogTitle>
            <DialogContent>
                <Box
                    component="form"
                    id="createExternalPricelistForm"
                    sx={{ display: 'flex', flexDirection: 'column' }}
                    onSubmit={handleSubmit(submit)}
                >
                    <Grid container spacing={2} direction="column">
                        <Grid item>
                            <FormTextField label="Name" autoFocus control={control} name="name" fullWidth />
                        </Grid>
                        <Grid item>
                            <FormTextField label="Reference" control={control} name="pricelistRef" />
                        </Grid>
                        <Grid item>
                            <FormTextField
                                control={control}
                                label="Margin Threshold (%)"
                                type="number"
                                name="marginThreshold"
                            />
                        </Grid>
                        <Grid item>
                            <FormAutocomplete
                                control={control}
                                name="inPricelistId"
                                label="Pricelist"
                                getOptionLabel={pricelist =>
                                    pricelists[pricelist] != null
                                        ? `${pricelists[pricelist!]?.pricelistRef} - ${
                                              pricelists[pricelist!].name
                                          }`
                                        : ''
                                }
                                options={Object.values(pricelists).map(c => c.pricelistId ?? null)}
                                clearOnEscape
                                placeholder="In pricelist"
                            />
                        </Grid>
                        <Grid>
                            <FormCheckbox
                                control={control}
                                name="includeVat"
                                label="Include VAT in margin calculations"
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <ProgressButton
                    color="primary"
                    type="submit"
                    form="createExternalPricelistForm"
                    disabled={!isDirty}
                    isLoading={isCreating}
                >
                    Create
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(CreateExternalPricelistDialog);
